import { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';

// Components
import CollapseCard from 'components/Collapse';
import GlobalAlert from 'components/Notification';
import RoamPairsTable from './components/RoamPairsTable';
import RoamSsidTable from './components/RoamSsidTable';

import SuccessRateBarChart from 'pages/AInsight/Roam/components/SuccessRateBarChart';
import RoamingSuccessRateInfo from 'pages/AInsight/Roam/components/RoamingSuccessRateInfo';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.svg';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

const RoamDrawer = ({ auth, standalone, successRateInfo }) => {
  const { sendBatch } = useDataSourceManager();
  const [roamPairStatsUuid, _setRoamPairStatsUuid] = useState(uuidv4());
  const [roamSsidStatsUuid, _setRoamssidStatsUuid] = useState(uuidv4());
  const [pairStatsList, setPairStatsList] = useState([]);
  const [ssidStatsList, setSsidStatsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const fetchPairStats = () => {
    const apiUrl = standalone
      ? `/orgs/:orgId/sites/:siteId/roam/successRate/pairStats.read`
      : `/ents/:entId/orgs/:orgId/sites/:siteId/roam/successRate/pairStats.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    return {
      apiKey: apiUrl,
      restfulPlaceholders: restfulParams,
    };
  };

  const fetchSsidStats = () => {
    const apiUrl = standalone
      ? `/orgs/:orgId/sites/:siteId/roam/successRate/ssidStats.read`
      : `/ents/:entId/orgs/:orgId/sites/:siteId/roam/successRate/ssidStats.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    return {
      apiKey: apiUrl,
      restfulPlaceholders: restfulParams,
    };
  };

  const parsePairStatsInfo = pairStatsData => {
    const tmpPairStatsData = cloneDeep(pairStatsData);
    const tmpPairStatsList = [];
    if (pairStatsData?.length > 0) {
      tmpPairStatsData.forEach((item, index) => {
        tmpPairStatsList.push({
          index: index + 1,
          apPairFromTo: `${item?.fromDeviceName} → ${item?.toDeviceName}`,
          roamEvents: item?.events,
          p95Latency: `${(item?.P95ms / 1000).toFixed(1)}s`,
          failRatio: `${item?.failRatePercent}%`,
        });
      });
    }

    setPairStatsList(tmpPairStatsList);
  };

  const parseSsidStatsInfo = ssidStatsData => {
    const tmpSsidStatsData = cloneDeep(ssidStatsData);
    const tmpSsidStatsList = [];
    if (ssidStatsData?.length > 0) {
      tmpSsidStatsData.forEach((item, index) => {
        tmpSsidStatsList.push({
          index: index + 1,
          apPairFromTo: item?.ssid,
          roamEvents: item?.events,
          successRate: `${item?.successRatePercent}%`,
          p95Latency: `${(item?.P95ms / 1000).toFixed(1)}s`,
        });
      });
    }
    setSsidStatsList(tmpSsidStatsList);
  };

  const init = async () => {
    setIsLoading(true);

    const pairStatsParams = fetchPairStats();
    const ssidStatsParams = fetchSsidStats();

    const { getSendBatchData } = await sendBatch([
      { ...pairStatsParams, requestUuid: roamPairStatsUuid },
      { ...ssidStatsParams, requestUuid: roamSsidStatsUuid },
    ]);

    const pairStatsRes = getSendBatchData({ requestUuid: roamPairStatsUuid })?.data?.data;
    const ssidStatsRes = getSendBatchData({ requestUuid: roamSsidStatsUuid })?.data?.data;

    if (pairStatsRes) {
      if (checkMockup) {
        setIsLoading(false);
        parsePairStatsInfo(pairStatsRes);
      } else {
        if (pairStatsRes.hasOwnProperty('code') && pairStatsRes?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: pairStatsRes?.message,
          });
          setPairStatsList([]);
        } else {
          setIsLoading(false);
          parsePairStatsInfo(pairStatsRes);
        }
      }
    } else {
      setIsLoading(false);
    }

    if (ssidStatsRes) {
      if (checkMockup) {
        setIsLoading(false);
        parseSsidStatsInfo(ssidStatsRes);
      } else {
        if (ssidStatsRes.hasOwnProperty('code') && ssidStatsRes?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: ssidStatsRes?.message,
          });
          setSsidStatsList([]);
        } else {
          setIsLoading(false);
          parseSsidStatsInfo(ssidStatsRes);
        }
      }
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_linebar_chart} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ color: '#172664', fontSize: 15 }}>Roaming Success Rate</Typography>
      </Box>
      <Box sx={{ marginBottom: 2 }}>
        <RoamingSuccessRateInfo successRateInfo={successRateInfo} />
      </Box>
      <CollapseCard title="Roaming Latency Distribution">
        <Box sx={{ width: '100%' }}>
          <SuccessRateBarChart barChartData={successRateInfo?.series} />
        </Box>
      </CollapseCard>
      <CollapseCard title="Worst AP Pairs (Top 5)">
        <RoamPairsTable isLoading={isLoading} pairStatsList={pairStatsList} />
      </CollapseCard>
      <CollapseCard title="Roaming Performance by SSID">
        <RoamSsidTable isLoading={isLoading} ssidStatsList={ssidStatsList} />
      </CollapseCard>

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </Box>
  );
};

export default RoamDrawer;
